// Email drafts: kinds and the recipient-isolation rules. Pure, no database.
//
// Read docs/apd-copilot/FEATURE_CHARTER.md before changing anything here.
//
// Copilot V1 NEVER SENDS EMAIL. It can prepare drafts, a person can edit them,
// and a person can press "Create Gmail draft", which places the draft in that
// mailbox's Drafts folder for the person to send from Gmail themselves.
//
// The isolation rules below are what make a batch of supplier RFQs safe:
//   * a supplier draft goes to ONE supplier, with no other supplier on it;
//   * no draft names or addresses a different supplier from the same batch or
//     the same RFQ;
//   * a customer draft names or addresses no supplier at all.
// A draft that breaks one is saved but cannot become a Gmail draft until a
// person edits it clean.

export const DRAFT_KINDS = [
  'supplier_rfq',
  'supplier_followup',
  'customer_reply',
  'customer_clarification',
  'internal',
] as const;
export type DraftKind = (typeof DRAFT_KINDS)[number];

export const DRAFT_KIND_LABEL: Record<DraftKind, string> = {
  supplier_rfq: 'Supplier RFQ',
  supplier_followup: 'Supplier follow-up',
  customer_reply: 'Customer reply',
  customer_clarification: 'Customer clarification',
  internal: 'Internal note',
};

export const isSupplierKind = (k: DraftKind) => k === 'supplier_rfq' || k === 'supplier_followup';
export const isCustomerKind = (k: DraftKind) => k === 'customer_reply' || k === 'customer_clarification';

export interface PartyIdentity {
  /** Company name as APD knows it. */
  name: string | null;
  emails: string[];
}

export interface DraftForCheck {
  key: string;
  kind: DraftKind;
  to: string[];
  cc?: string[];
  subject: string;
  body: string;
  /** The supplier this draft is for, when it is a supplier draft. */
  recipientLabel?: string | null;
}

export interface IsolationIssue {
  key: string;
  code: 'supplier_multiple_recipients' | 'names_other_supplier' | 'addresses_other_supplier'
    | 'customer_draft_names_supplier' | 'customer_draft_addresses_supplier' | 'duplicate_recipient';
  message: string;
}

const lc = (s: string) => s.trim().toLowerCase();
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Company names shorter than this are too likely to match ordinary words. */
const MIN_NAME = 4;
/** Legal-form suffixes: not part of what identifies a supplier. */
const LEGAL = /\b(inc|llc|l\.l\.c|ltd|limited|corp|corporation|co|company|gmbh|sa|sas|bv|plc|pty|srl|ag)\b\.?/gi;
/** Words too generic to identify a supplier on their own. */
const GENERIC = new Set(['aero', 'aviation', 'aerospace', 'air', 'aircraft', 'supply', 'supplies', 'parts', 'spares', 'global',
  'international', 'intl', 'services', 'service', 'technologies', 'technology', 'group', 'industries', 'the', 'and', 'trading']);

/** The supplier's name without its legal form, if long enough to be telling. */
export function distinctiveName(name: string | null | undefined): string | null {
  if (!name) return null;
  const core = name.replace(LEGAL, ' ').replace(/[^\p{L}\p{N}&' -]/gu, ' ').replace(/\s+/g, ' ').trim();
  if (core.length < MIN_NAME) return null;
  // A lone generic word ("Aero") identifies nobody; a whole phrase does.
  if (!core.includes(' ') && GENERIC.has(core.toLowerCase())) return null;
  return core;
}

/** Every phrase that would identify the supplier in text: the core name, and
 *  its first word when that word is distinctive on its own ("Collins"). */
export function identifyingPhrases(name: string | null | undefined): string[] {
  const core = distinctiveName(name);
  if (!core) return [];
  const out = [core];
  const first = core.split(' ')[0] ?? '';
  if (core.includes(' ') && first.length >= 5 && !GENERIC.has(first.toLowerCase())) out.push(first);
  return out;
}

function mentions(text: string, party: PartyIdentity): { name: boolean; email: boolean } {
  const t = text.toLowerCase();
  const email = party.emails.some((e) => e && t.includes(lc(e)));
  const name = identifyingPhrases(party.name).some((p) =>
    new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRe(p.toLowerCase())}($|[^\\p{L}\\p{N}])`, 'u').test(t));
  return { name, email };
}

/**
 * Check a set of drafts against the suppliers and customer known for the deal.
 * `suppliers` should include every supplier APD has contacted on the RFQ plus
 * every supplier a draft in the batch is addressed to.
 */
export function checkDraftIsolation(drafts: DraftForCheck[], suppliers: PartyIdentity[]): IsolationIssue[] {
  const issues: IsolationIssue[] = [];
  const seenRecipients = new Map<string, string>();

  for (const d of drafts) {
    const recipients = [...d.to, ...(d.cc ?? [])].map(lc);
    for (const r of d.to.map(lc)) {
      const prior = seenRecipients.get(r);
      if (prior && prior !== d.key) {
        issues.push({ key: d.key, code: 'duplicate_recipient', message: `${r} already has a draft in this batch.` });
      }
      seenRecipients.set(r, d.key);
    }

    if (isSupplierKind(d.kind)) {
      if (recipients.length !== 1) {
        issues.push({ key: d.key, code: 'supplier_multiple_recipients', message: 'A supplier draft goes to exactly one address, with no copies.' });
      }
      // The supplier this draft is FOR: matched by its To address, else by label.
      // A supplier only on Cc is not the recipient; it is a leak.
      const toOnly = d.to.map(lc);
      const own = suppliers.filter((s) => s.emails.some((e) => toOnly.includes(lc(e)))
        || (!!d.recipientLabel && !!s.name && lc(s.name) === lc(d.recipientLabel)));
      const others = suppliers.filter((s) => !own.includes(s));
      const text = `${d.subject}\n${d.body}`;
      for (const o of others) {
        const m = mentions(text, o);
        if (m.email || o.emails.some((e) => recipients.includes(lc(e)))) {
          issues.push({ key: d.key, code: 'addresses_other_supplier', message: `Mentions or copies another supplier's address (${o.name ?? 'supplier'}).` });
        } else if (m.name) {
          issues.push({ key: d.key, code: 'names_other_supplier', message: `Names another supplier (${o.name}).` });
        }
      }
    }

    if (isCustomerKind(d.kind)) {
      const text = `${d.subject}\n${d.body}`;
      for (const s of suppliers) {
        const m = mentions(text, s);
        if (m.email || s.emails.some((e) => recipients.includes(lc(e)))) {
          issues.push({ key: d.key, code: 'customer_draft_addresses_supplier', message: `Contains a supplier's address (${s.name ?? 'supplier'}).` });
        } else if (m.name) {
          issues.push({ key: d.key, code: 'customer_draft_names_supplier', message: `Names a supplier (${s.name}). Customers must not see who we source from.` });
        }
      }
    }
  }
  return issues;
}

/** The exact content a person approves. Any change produces a different string
 *  and therefore a different hash, so an approval cannot outlive an edit. */
export function canonicalDraftContent(d: {
  mailboxId: string | null; to: string[]; cc: string[]; subject: string; body: string; replyToGmailThreadId: string | null;
}): string {
  return JSON.stringify({
    mailbox: d.mailboxId,
    to: d.to.map(lc),
    cc: d.cc.map(lc),
    subject: d.subject,
    body: d.body,
    thread: d.replyToGmailThreadId,
  });
}

/** Whether an address's domain is the page's host, or one is a subdomain of
 *  the other (sales@acme.com on www.acme.com). A label, not a gate: suppliers
 *  are also listed in directories on other domains. */
export function domainMatchesSource(email: string, url: string): boolean {
  const domain = lc(email).split('@')[1] ?? '';
  let host: string;
  try { host = new URL(url).hostname.toLowerCase(); } catch { return false; }
  if (!domain.includes('.') || !host.includes('.')) return false;
  return domain === host || host.endsWith(`.${domain}`) || domain.endsWith(`.${host}`);
}

/** The web-found recipients a person must confirm before a draft is saved:
 *  those still among its To and Cc addresses. */
export function webRecipientsToConfirm<T extends { email: string }>(d: { to: string[]; cc?: string[]; webRecipients?: T[] }): T[] {
  const recipients = new Set([...d.to, ...(d.cc ?? [])].map(lc));
  return (d.webRecipients ?? []).filter((w) => recipients.has(lc(w.email)));
}
